/* crm-reports.jsx — Phase 5: Report Builder (22) + Executive Dashboards (23)
   Bridged component — no imports, all via window globals. */

const { useState, useEffect, useMemo, useCallback, useRef } = React;

/* ── helpers ── */
const _fmt = (n) => n == null ? '—' : n >= 1e6 ? `$${(n/1e6).toFixed(1)}M` : n >= 1e3 ? `$${(n/1e3).toFixed(0)}K` : `$${Number(n).toLocaleString()}`;
const _pct = (n) => n == null ? '—' : `${Math.round(n)}%`;
const _date = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const ENTITY_TYPES = ['deals', 'companies', 'people', 'activities', 'tasks'];
const CHART_TYPES  = ['BAR', 'LINE', 'PIE'];
// QuerySpec operator vocabulary (BE-03: 'equals' replaced by 'eq')
const OPERATORS    = ['eq', 'contains', 'gt', 'lt', 'is_set', 'is_not_set'];

// Registry field keys per entity (BE-04: aligned with analytics-schema.registry.ts)
const COLUMNS_MAP = {
  deals:      ['value', 'probability', 'status', 'expectedCloseDate', 'stage.name', 'stage.kind', 'owner.name', 'company.name', 'pipeline.name'],
  companies:  ['industry', 'lifecycle', 'employees', 'owner', 'createdAt'],
  people:     ['status', 'createdAt', 'company.name', 'owner', 'score'],
  activities: ['type', 'actor', 'createdAt'],
  tasks:      ['status', 'priority', 'dueDate', 'assignee.name', 'createdAt'],
};

// Only fields with groupable: true in the registry (subset of COLUMNS_MAP)
const GROUPABLE_MAP = {
  deals:      ['status', 'expectedCloseDate', 'stage.name', 'stage.kind', 'owner.name', 'company.name', 'pipeline.name'],
  companies:  ['industry', 'lifecycle', 'owner', 'createdAt'],
  people:     ['status', 'createdAt', 'company.name', 'owner'],
  activities: ['type', 'actor', 'createdAt'],
  tasks:      ['status', 'priority', 'dueDate', 'assignee.name', 'createdAt'],
};

// Mirror of backend normalizeKey: dots→underscores, camelCase→snake_case
const toRowKey = (key) =>
  (key || '').replace(/\./g, '_').replace(/([A-Z])/g, c => '_' + c.toLowerCase()).replace(/^_/, '');

const CHART_ICON = { BAR: 'BarChart2', LINE: 'TrendingUp', PIE: 'PieChart' };

/* ── card styles ── */
const card = { background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 10 };
const inputSx = { padding: '6px 10px', border: '1px solid var(--nx-border)', borderRadius: 6, background: 'var(--nx-bg)', color: 'var(--nx-text)', fontSize: 12, width: '100%', boxSizing: 'border-box' };
const btnPrimary = { padding: '6px 14px', borderRadius: 6, border: 'none', background: 'var(--nx-accent)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const btnGhost = { padding: '6px 12px', borderRadius: 6, border: '1px solid var(--nx-border)', background: 'transparent', color: 'var(--nx-text)', fontSize: 12, cursor: 'pointer' };

/* ══════════════════════════════════════════════════════
   ReportColumnPicker
   ══════════════════════════════════════════════════════ */
const ReportColumnPicker = ({ entityType, selected, onChange }) => {
  const cols = COLUMNS_MAP[entityType] || [];
  const toggle = (col) => onChange(selected.includes(col) ? selected.filter(c => c !== col) : [...selected, col]);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {cols.map(col => (
        <button key={col} onClick={() => toggle(col)} style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: '1px solid ' + (selected.includes(col) ? 'var(--nx-accent)' : 'var(--nx-border)'), background: selected.includes(col) ? 'var(--nx-accent)' : 'transparent', color: selected.includes(col) ? '#fff' : 'var(--nx-text-muted)' }}>
          {col}
        </button>
      ))}
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   ReportFilterRow
   ══════════════════════════════════════════════════════ */
const ReportFilterRow = ({ entityType, filter, onChange, onRemove }) => {
  const cols = COLUMNS_MAP[entityType] || [];
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <select value={filter.field} onChange={e => onChange({ ...filter, field: e.target.value })} style={{ ...inputSx, width: 120 }}>
        <option value="">Field...</option>
        {cols.map(c => <option key={c} value={c}>{c}</option>)}
      </select>
      <select value={filter.operator} onChange={e => onChange({ ...filter, operator: e.target.value })} style={{ ...inputSx, width: 110 }}>
        {OPERATORS.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
      {!['is_set','is_not_set'].includes(filter.operator) && (
        <input value={filter.value || ''} onChange={e => onChange({ ...filter, value: e.target.value })} placeholder="Value" style={{ ...inputSx, width: 120 }} />
      )}
      <button onClick={onRemove} style={{ ...btnGhost, padding: '4px 8px', color: '#ef4444' }}>
        <NexusIcon name="X" size={12} />
      </button>
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   ReportChartPreview
   ══════════════════════════════════════════════════════ */
const ReportChartPreview = ({ data = [], chartType = 'BAR', labelKey = 'label', valueKey = 'value' }) => {
  if (!data.length) return <div style={{ textAlign: 'center', color: 'var(--nx-text-muted)', fontSize: 12, padding: 20 }}>No data to preview</div>;
  const W = 300, H = 120, pad = 20;
  const vals = data.map(d => Number(d[valueKey]) || 0);
  const maxV = Math.max(...vals, 1);
  const n = data.length;

  if (chartType === 'BAR') {
    const bw = Math.floor((W - pad * 2) / n) - 4;
    return (
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', maxHeight: 120 }}>
        {data.map((d, i) => {
          const bh = Math.max(2, ((vals[i] / maxV) * (H - pad - 16)));
          const x = pad + i * ((W - pad * 2) / n) + 2;
          const y = H - pad - bh;
          return (
            <g key={i}>
              <rect x={x} y={y} width={bw} height={bh} rx={2} fill="var(--nx-accent)" opacity={0.85} />
              <text x={x + bw / 2} y={H - 4} textAnchor="middle" fontSize={8} fill="var(--nx-text-muted)">{String(d[labelKey]).slice(0,6)}</text>
            </g>
          );
        })}
      </svg>
    );
  }

  if (chartType === 'LINE') {
    const xs = data.map((_, i) => pad + i * ((W - pad * 2) / Math.max(n - 1, 1)));
    const ys = vals.map(v => H - pad - ((v / maxV) * (H - pad - 10)));
    const pts = xs.map((x, i) => `${x},${ys[i]}`).join(' ');
    return (
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', maxHeight: 120 }}>
        <polyline points={pts} fill="none" stroke="var(--nx-accent)" strokeWidth={2} />
        {xs.map((x, i) => <circle key={i} cx={x} cy={ys[i]} r={3} fill="var(--nx-accent)" />)}
      </svg>
    );
  }

  if (chartType === 'PIE') {
    const cx = W / 2, cy = H / 2, r = Math.min(cx, cy) - 10;
    const total = vals.reduce((a, b) => a + b, 0) || 1;
    const colors = ['var(--nx-accent)','#8B5CF6','#06b6d4','#10b981','#f59e0b','#ef4444'];
    let angle = -Math.PI / 2;
    return (
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', maxHeight: 120 }}>
        {data.map((d, i) => {
          const slice = (vals[i] / total) * 2 * Math.PI;
          const x1 = cx + r * Math.cos(angle), y1 = cy + r * Math.sin(angle);
          angle += slice;
          const x2 = cx + r * Math.cos(angle), y2 = cy + r * Math.sin(angle);
          const large = slice > Math.PI ? 1 : 0;
          return <path key={i} d={`M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${large},1 ${x2},${y2} Z`} fill={colors[i % colors.length]} opacity={0.85} />;
        })}
      </svg>
    );
  }

  return null;
};

/* ══════════════════════════════════════════════════════
   ReportBuilderView
   ══════════════════════════════════════════════════════ */
const ReportBuilderView = ({ reportId, onSaved, onCancel }) => {
  const wsId = window.__nx_ws?.id || window.__nx_ws?.workspaceId;
  const toast = useToast();
  const [name, setName] = useState('');
  const [entityType, setEntityType] = useState('deals');
  const [columns, setColumns] = useState(['title', 'value', 'stage']);
  const [filters, setFilters] = useState([]);
  const [groupBy, setGroupBy] = useState('');
  const [chartType, setChartType] = useState('BAR');
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);
  const [queryColumns, setQueryColumns] = useState([]);
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    if (!wsId || !reportId) return;
    window.__nx_api?.reports?.getOne(wsId, reportId).then(r => {
      if (!r) return;
      setName(r.name || ''); setEntityType(r.entityType || 'deals');
      setColumns(r.columns || []); setFilters(r.filters || []);
      setGroupBy(r.groupBy || ''); setChartType(r.chartType || 'BAR');
    }).catch(() => {});
  }, [reportId, wsId]);

  const addFilter = () => setFilters(f => [...f, { field: '', operator: 'eq', value: '' }]);
  const updateFilter = (i, v) => setFilters(f => f.map((x, idx) => idx === i ? v : x));
  const removeFilter = (i) => setFilters(f => f.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    if (!name.trim()) { toast.show({ title: 'Report name is required', type: 'error' }); return; }
    setSaving(true);
    try {
      const dto = { name, entityType, columns, filters, groupBy, chartType };
      if (reportId) await window.__nx_api?.reports?.update(wsId, reportId, dto);
      else await window.__nx_api?.reports?.create(wsId, dto);
      toast.show({ title: 'Report saved', type: 'success' });
      onSaved?.();
    } catch (e) { toast.show({ title: e.message, type: 'error' }); }
    setSaving(false);
  };

  const handleRun = async () => {
    if (!reportId) return;
    setRunning(true);
    try {
      // data is QueryResult: { columns, rows, total, truncated, metadata, report, warnings? }
      const data = await window.__nx_api?.reports?.run(wsId, reportId);
      const rows = data?.rows || [];
      const qCols = data?.columns || [];
      setResults(rows);
      setQueryColumns(qCols);
      // Chart: first non-agg column as label, first agg column as value
      const labelCol = qCols.find(c => !c.isAggregation);
      const valueCol = qCols.find(c => c.isAggregation);
      const labelKey = labelCol?.key || toRowKey(groupBy) || 'label';
      const valueKey = valueCol?.key || 'count';
      setChartData(rows.slice(0, 8).map(r => ({
        label: String(r[labelKey] ?? '?'),
        value: Number(r[valueKey] ?? 0),
      })));
    } catch (e) { toast.show({ title: e.message || 'Run failed', type: 'error' }); }
    setRunning(false);
  };

  return (
    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 700, fontSize: 15 }}>{reportId ? 'Edit Report' : 'New Report'}</span>
        <div style={{ display: 'flex', gap: 8 }}>
          {onCancel && <button style={btnGhost} onClick={onCancel}>Cancel</button>}
          <button style={btnPrimary} onClick={handleRun} disabled={running}>{running ? 'Running...' : 'Run'}</button>
          <button style={btnPrimary} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div><label style={{ fontSize: 11, color: 'var(--nx-text-muted)', display: 'block', marginBottom: 4 }}>Report Name</label>
          <input style={inputSx} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Q3 Pipeline by Stage" /></div>
        <div><label style={{ fontSize: 11, color: 'var(--nx-text-muted)', display: 'block', marginBottom: 4 }}>Entity Type</label>
          <select style={inputSx} value={entityType} onChange={e => { setEntityType(e.target.value); setColumns([]); setGroupBy(''); }}>
            {ENTITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select></div>
      </div>

      <div style={{ ...card, padding: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 8, color: 'var(--nx-text-muted)' }}>COLUMNS</div>
        <ReportColumnPicker entityType={entityType} selected={columns} onChange={setColumns} />
      </div>

      <div style={{ ...card, padding: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)' }}>FILTERS</span>
          <button style={{ ...btnGhost, padding: '3px 10px', fontSize: 11 }} onClick={addFilter}>+ Add Filter</button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {filters.map((f, i) => <ReportFilterRow key={i} entityType={entityType} filter={f} onChange={v => updateFilter(i, v)} onRemove={() => removeFilter(i)} />)}
          {!filters.length && <span style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>No filters applied</span>}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div><label style={{ fontSize: 11, color: 'var(--nx-text-muted)', display: 'block', marginBottom: 4 }}>Group By</label>
          <select style={inputSx} value={groupBy} onChange={e => setGroupBy(e.target.value)}>
            <option value="">None</option>
            {(GROUPABLE_MAP[entityType] || []).map(c => <option key={c} value={c}>{c}</option>)}
          </select></div>
        <div><label style={{ fontSize: 11, color: 'var(--nx-text-muted)', display: 'block', marginBottom: 4 }}>Chart Type</label>
          <select style={inputSx} value={chartType} onChange={e => setChartType(e.target.value)}>
            {CHART_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select></div>
      </div>

      {chartData.length > 0 && (
        <div style={{ ...card, padding: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 8, color: 'var(--nx-text-muted)' }}>CHART PREVIEW</div>
          <ReportChartPreview data={chartData} chartType={chartType} />
        </div>
      )}

      {results && (
        <div style={{ ...card, overflow: 'auto' }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--nx-border)', fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)' }}>RESULTS ({results.length} rows)</div>
          {results.length === 0 ? <div style={{ padding: 16, textAlign: 'center', color: 'var(--nx-text-muted)', fontSize: 12 }}>No results</div> : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  {queryColumns.map(c => (
                    <th key={c.key} style={{ padding: '6px 12px', textAlign: 'left', background: 'var(--nx-surface)', borderBottom: '1px solid var(--nx-border)', color: 'var(--nx-text-muted)', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {c.label.toUpperCase()}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {results.slice(0, 50).map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--nx-border)' }}>
                    {queryColumns.map(c => (
                      <td key={c.key} style={{ padding: '6px 12px' }}>{String(row[c.key] ?? '—').slice(0, 40)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   ReportCard
   ══════════════════════════════════════════════════════ */
const ReportCard = ({ report, onRun, onEdit }) => (
  <div style={{ ...card, padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
      <NexusIcon name={CHART_ICON[report.chartType] || 'BarChart2'} size={16} style={{ color: 'var(--nx-accent)', marginTop: 1, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{report.name}</div>
        <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>{report.entityType} · {_date(report.createdAt)}</div>
      </div>
    </div>
    <div style={{ display: 'flex', gap: 6 }}>
      <button style={{ ...btnPrimary, flex: 1, padding: '5px 8px', fontSize: 11 }} onClick={() => onRun?.(report)}>Run</button>
      <button style={{ ...btnGhost, padding: '5px 8px', fontSize: 11 }} onClick={() => onEdit?.(report)}>Edit</button>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════
   ReportsListView
   ══════════════════════════════════════════════════════ */
const ReportsListView = () => {
  const wsId = window.__nx_ws?.id || window.__nx_ws?.workspaceId;
  const toast = useToast();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // 'list' | 'builder'
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    window.__nx_api?.reports?.list(wsId).then(r => setReports(Array.isArray(r) ? r : r?.data || [])).catch(() => setReports([])).finally(() => setLoading(false));
  }, [wsId]);

  useEffect(() => { load(); }, [load]);

  if (view === 'builder') {
    return <ReportBuilderView reportId={editId} onSaved={() => { setView('list'); load(); }} onCancel={() => setView('list')} />;
  }

  return (
    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 700, fontSize: 15 }}>Saved Reports</span>
        <button style={btnPrimary} onClick={() => { setEditId(null); setView('builder'); }}>+ New Report</button>
      </div>
      {loading ? <div style={{ color: 'var(--nx-text-muted)', fontSize: 13 }}>Loading...</div> : (
        reports.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--nx-text-muted)' }}>
            <NexusIcon name="BarChart2" size={32} style={{ marginBottom: 12, opacity: 0.3 }} />
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>No reports yet</div>
            <div style={{ fontSize: 12 }}>Create your first report to get started</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
            {reports.map(r => <ReportCard key={r.id} report={r} onRun={() => { setEditId(r.id); setView('builder'); }} onEdit={() => { setEditId(r.id); setView('builder'); }} />)}
          </div>
        )
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   ExecutiveDashboardView
   ══════════════════════════════════════════════════════ */
const ExecutiveDashboardView = () => {
  const store = useStore?.();
  const deals = useMemo(() => {
    const all = store?.deals || [];
    return Array.isArray(all) ? all : Object.values(all);
  }, [store?.deals]);

  const kpis = useMemo(() => {
    const total = deals.length;
    const pipeVal = deals.filter(d => !['WON','LOST'].includes(d.stage?.kind || d.stageKind || '')).reduce((s, d) => s + (Number(d.value) || 0), 0);
    const avgSize = total ? Math.round(deals.reduce((s, d) => s + (Number(d.value) || 0), 0) / total) : 0;
    const won = deals.filter(d => (d.stage?.kind || d.stageKind || d.stage || '').toUpperCase() === 'WON').length;
    const closed = deals.filter(d => ['WON','LOST'].includes((d.stage?.kind || d.stageKind || d.stage || '').toUpperCase())).length;
    const winRate = closed ? Math.round((won / closed) * 100) : 0;
    return { total, pipeVal, avgSize, winRate };
  }, [deals]);

  /* revenue trend: last 6 months */
  const revTrend = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 6 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
      const label = d.toLocaleString('default', { month: 'short' });
      const val = deals.filter(dx => {
        const cd = dx.closedAt ? new Date(dx.closedAt) : null;
        return cd && cd.getFullYear() === d.getFullYear() && cd.getMonth() === d.getMonth() && (dx.stage?.kind || dx.stageKind || '').toUpperCase() === 'WON';
      }).reduce((s, dx) => s + (Number(dx.value) || 0), 0);
      return { label, value: val };
    });
  }, [deals]);

  /* stage distribution */
  const stageDist = useMemo(() => {
    const map = {};
    deals.forEach(d => {
      const s = d.stage?.name || d.stageName || d.stage || 'Unknown';
      map[s] = (map[s] || 0) + 1;
    });
    return Object.entries(map).map(([label, value]) => ({ label, value })).slice(0, 7);
  }, [deals]);

  const kpiCard = (icon, label, val, color = 'var(--nx-accent)') => (
    <div style={{ ...card, padding: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <NexusIcon name={icon} size={14} style={{ color }} />
        <span style={{ fontSize: 11, color: 'var(--nx-text-muted)', fontWeight: 600 }}>{label}</span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{val}</div>
    </div>
  );

  return (
    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ fontWeight: 700, fontSize: 15 }}>Executive Dashboard</div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        {kpiCard('Briefcase', 'Total Deals', kpis.total, 'var(--nx-accent)')}
        {kpiCard('DollarSign', 'Pipeline Value', _fmt(kpis.pipeVal), '#10b981')}
        {kpiCard('TrendingUp', 'Avg Deal Size', _fmt(kpis.avgSize), '#f59e0b')}
        {kpiCard('Award', 'Win Rate', _pct(kpis.winRate), '#8B5CF6')}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div style={{ ...card, padding: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
            <NexusIcon name="TrendingUp" size={14} style={{ color: 'var(--nx-accent)' }} /> Revenue Trend (6M)
          </div>
          <ReportChartPreview data={revTrend} chartType="LINE" />
        </div>
        <div style={{ ...card, padding: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
            <NexusIcon name="BarChart2" size={14} style={{ color: 'var(--nx-accent)' }} /> Stage Distribution
          </div>
          <ReportChartPreview data={stageDist} chartType="BAR" />
        </div>
      </div>
    </div>
  );
};

/* ── window registrations ── */
window.ReportBuilderView   = ReportBuilderView;
window.ReportsListView     = ReportsListView;
window.ExecutiveDashboardView = ExecutiveDashboardView;
